import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function StaffListPage() {
  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/staff');
        setStaffList(res.data.data || []);
      } catch (e) { toast.error('Failed to load staff'); }
    };
    fetchData();
  }, []);

  const filtered = staffList.filter(s => {
    if (complianceFilter !== 'all' && s.compliance_status !== complianceFilter) return false;
    if (search && !s.full_name?.toLowerCase().includes(search.toLowerCase()) && !s.email?.toLowerCase().includes(search.toLowerCase()) && !s.employee_id?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Staff Profiles</h1>
        <p className="text-muted-foreground mt-1">Staff compliance and profile management</p>
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="staff-search" />
          </div>
          <Select value={complianceFilter} onValueChange={setComplianceFilter}>
            <SelectTrigger className="w-[180px]" data-testid="compliance-filter"><SelectValue placeholder="Compliance" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Compliance</SelectItem>
              <SelectItem value="COMPLIANT">Compliant</SelectItem>
              <SelectItem value="NON_COMPLIANT">Non-Compliant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} staff member(s)</p>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Employee ID</TableHead><TableHead>Compliance</TableHead><TableHead>Branch</TableHead><TableHead>Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No staff found</TableCell></TableRow>
              : filtered.map(s => (
                <TableRow key={s.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/staff/${s.user_id || s.id}`)}>
                  <TableCell className="font-medium">{s.full_name || 'N/A'}</TableCell>
                  <TableCell className="font-mono text-xs">{s.employee_id || '-'}</TableCell>
                  <TableCell><StatusBadge status={s.compliance_status === 'COMPLIANT' ? 'APPROVED' : 'REJECTED'} /></TableCell>
                  <TableCell>{s.branch_id || '-'}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" data-testid={`staff-edit-${s.id}`}>View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
